import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Box, Button, useTheme } from '@mui/material';
import { Controller } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector, useStalePlanRefresh } from '../../common/hooks';
import { ComboSelect, TextboxInput, Typography } from '../../components/atoms';
import { EditableTable } from '../../components/molecules/EditableTable';
import { InfoTooltip } from '../../components/molecules/InfoTooltip';
import { ConfirmationCentered } from '../../components/molecules/ConfirmationDialog/ConfirmationDialog';
import { AutoNameField, formatAccountName } from '../../components/molecules/AccountNameField/AccountNameField';
import { TermTypeCell } from '../../components/molecules/TermTypeCell/TermTypeCell';
import { TermTypeSelect } from '../../components/molecules/TermTypeSelect/TermTypeSelect';
import type { TermOptionItem } from '../../components/molecules/TermTypeSelect/TermTypeSelect';
import type { EditableColumnDef, RowEditContext } from '../../components/molecules/EditableTable';
import {
    ContributionsEditor,
    STORAGE_DATE_FORMAT
} from '../../components/organisms/ContributionsEditor/ContributionsEditor';
import type { OptionItem } from '../../components/organisms/ContributionsEditor/ContributionsEditor';
import {
    CONTRIBUTION_PERIOD_OPTIONS,
    deriveContributionPeriodKey as deriveContributionPeriodKeyFromRules,
    getContributionPeriodDates as getContributionPeriodDatesFromRules,
    IncomeSorceDefaultPeriodMapping
} from '../../common/contributionPeriodOptionRules';
import type {
    ContributionPeriodDateContext,
    ContributionPeriodDates
} from '../../common/contributionPeriodOptionRules';
import {
    deleteClientAccount,
    hydrateClientAccounts,
    persistClientAccounts,
    updateAccountState
} from '../../features/client-accounts';
import { syncAdviceGoals } from '../../features/client-goals';
import { setAffectedEwsTerms } from '../../features/ews';
import { setHasUnsavedProfileChanges, setHasOpenAccountsEdit } from '../../features/global/profileEditSlice';
import type { ClientAccountEntry } from '../../common/types/clients-entity';
import {
    checkFFEnabled,
    exceedsDigitLimit,
    formatCurrency,
    getClientJourneyFlowType,
    isMonthYearDatesEnabled,
    removeNonNumeric
} from '../../utils';
import { addAgeToDate } from '../../common/helper';
import {
    DEFAULT_CURRENCY_SYMBOL,
    DEFAULT_PLANNING_AGE,
    DEFAULT_RETIREMENT_AGE,
    EWS_DURATION_KEYS,
    FEATURE_NAMES,
    STATUSVARIANTS
} from '../../common/constants';
import { PdfDownloadErrorBanner } from '../../components/molecules/PdfDownloadErrorBanner/PdfDownloadErrorBanner';
import { startLoading, stopLoading } from '../../features/global/appLoader';
import { StalePlanBanner } from '../../components/molecules/StalePlanBanner/StalePlanBanner';

const FALLBACK_ACCOUNT_TYPES = ['Bank Account', 'Investment Account', 'Model Portfolio', 'Retirement Account'];

export const AccountsInlineTable = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const language = i18n.language;

    const accounts = useAppSelector((state) => state.adviceClientAccounts.accounts);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const orgInfo = useAppSelector((state) => state.global.orgInfo);
    const isFlow2 = getClientJourneyFlowType(adviceClient?.tenant) === 'flow2';

    // Any add/edit/delete of an account or its contributions (income) in Step 1 invalidates a
    // previously run plan: force a full EWS re-run from the Funding Navigator step (which in turn
    // re-triggers Goal Calculator and Plan Execution / Trade Instructions). Mirrors the sibling
    // PersonalInfoCardInline behaviour so profile and account/income edits stay consistent.
    const markPlanStaleForStep1Change = useCallback(() => {
        dispatch(setAffectedEwsTerms([...Object.values(EWS_DURATION_KEYS)]));
        dispatch(
            syncAdviceGoals({
                ...adviceGoals,
                response: [],
                global: {
                    ...adviceGoals.global,
                    responseGoalCalculator: [],
                    ...(isFlow2 && { status: STATUSVARIANTS.INCOMPLETE }),
                    enablePDFGenerate: false
                }
            })
        );
    }, [dispatch, adviceGoals, isFlow2]);
    const currencySymbol =
        useAppSelector((state) => state.global.globalConfig?.currencySymbol) || DEFAULT_CURRENCY_SYMBOL;

    const [pendingDeleteAccount, setPendingDeleteAccount] = useState<ClientAccountEntry | null>(null);
    // Message shown in the error banner (and drives the save-action exclamation) after a failed save.
    const [saveErrorMessage, setSaveErrorMessage] = useState('');
    // External error surfaced on the accountName field when a duplicate is detected on save.
    const [duplicateNameError, setDuplicateNameError] = useState('');
    // Add-account form is controlled here so the "Add Account" button can live in the header.
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [hasOpenEdit, setHasOpenEdit] = useState(false);

    // AEX-3363: stale-plan banner state; on "Refresh Plan" clear any lingering error messages and
    // re-seed the inline Accounts slice from the refreshed adviceClient. This slice is otherwise
    // only hydrated on mount, so without this the table would keep showing the pre-refresh accounts.
    const handlePlanRefreshed = useCallback(() => {
        setSaveErrorMessage('');
        setDuplicateNameError('');
        dispatch(hydrateClientAccounts());
    }, [dispatch]);
    const { isStalePlan, markStaleFromError, refreshPlan } = useStalePlanRefresh(handlePlanRefreshed);

    // On first load, seed the inline table from the persisted accounts
    // (adviceClient.accounts.investmentsSources); otherwise fall back to the initial state.
    useEffect(() => {
        dispatch(hydrateClientAccounts());
    }, []);

    // Mirror EditableTable's previous behaviour: auto-open the add form when there are no accounts.
    const [autoAddReady, setAutoAddReady] = useState(false);
    useEffect(() => {
        setAutoAddReady(true);
    }, []);
    useEffect(() => {
        if (autoAddReady && accounts.length === 0 && !isAddingNew) {
            setIsAddingNew(true);
        }
    }, [autoAddReady, accounts.length, isAddingNew]);

    // Clear the shared "unsaved changes"/"edit open" flags when the table unmounts (e.g. leaving
    // Step 1 or switching to the Profile tab, which unmounts this panel).
    useEffect(() => {
        return () => {
            dispatch(setHasUnsavedProfileChanges(false));
            dispatch(setHasOpenAccountsEdit(false));
        };
    }, [dispatch]);

    // Mirror the Profile tab: an in-progress add/edit with actual changes marks Step 1 dirty so
    // navigating away (tab switch / stepper) surfaces the Stay/Leave confirmation.
    const handleDirtyStateChange = useCallback(
        (dirty: boolean) => {
            dispatch(setHasUnsavedProfileChanges(dirty));
        },
        [dispatch]
    );

    // An account/income row being open in add/edit mode (regardless of dirty state) disables
    // "Continue to Next Step" on Step 1 the moment the user enters edit mode.
    const handleEditingChange = useCallback(
        (isEditing: boolean) => {
            setHasOpenEdit(isEditing);
            dispatch(setHasOpenAccountsEdit(isEditing));
        },
        [dispatch]
    );

    const typeOptions: TermOptionItem[] = useMemo(() => {
        const rawTypes = orgInfo?.investmentAccountType?.[language] || orgInfo?.investmentAccountType?.['en'] || [];
        if (Array.isArray(rawTypes) && rawTypes.length) {
            return rawTypes.map((item: { label: string; value: string; duration?: string }) => ({
                label: item.label,
                value: item.value,
                duration: item.duration
            }));
        }
        return FALLBACK_ACCOUNT_TYPES.map((label) => ({ label, value: label }));
    }, [orgInfo, language]);

    const typeLabelByValue = useMemo(
        () => typeOptions.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {} as Record<string, string>),
        [typeOptions]
    );
    const typeTermByValue = useMemo(
        () => typeOptions.reduce((acc, o) => ({ ...acc, [o.value]: o.duration ?? '' }), {} as Record<string, string>),
        [typeOptions]
    );

    const accountHolderOptions: OptionItem[] = useMemo(() => {
        const relationships = adviceClient?.relationship ?? [];
        const clientName = `${adviceClient?.clientFirstName ?? ''} ${adviceClient?.clientLastName ?? ''}`.trim();
        const options: OptionItem[] = [
            { label: clientName || t('CLIENT'), value: 'primary', firstName: adviceClient?.clientFirstName }
        ];
        relationships.forEach((rel) => {
            const relName = `${rel?.firstName ?? ''} ${rel?.lastName ?? ''}`.trim();
            if (rel?.id) options.push({ label: relName || t('CO-CLIENT'), value: rel.id, firstName: rel?.firstName });
        });
        if (clientName && relationships.length > 0) {
            options.push({ label: t('TEXT_JOINT'), value: 'joint', firstName: t('TEXT_JOINT') });
        }
        return options;
    }, [adviceClient, t]);

    const accountHolderLabelByValue: Record<string, string> = useMemo(
        () =>
            accountHolderOptions.reduce(
                (acc, opt) => ({ ...acc, [opt.value]: opt.label }),
                {} as Record<string, string>
            ),
        [accountHolderOptions]
    );

    const accountHolderFirstNameByValue: Record<string, string> = useMemo(
        () =>
            accountHolderOptions.reduce(
                (acc, opt) => ({ ...acc, [opt.value]: opt.firstName ?? '' }),
                {} as Record<string, string>
            ),
        [accountHolderOptions]
    );

    const getRetirementIsoDate = useMemo(
        () =>
            (accountHolderCode: string): string => {
                let dob: string | undefined;
                let age: string | number | undefined;
                if (accountHolderCode === 'primary' || accountHolderCode === 'joint') {
                    dob = adviceClient?.clientDob;
                    age = adviceClient?.retirementAge || DEFAULT_RETIREMENT_AGE;
                } else {
                    const rel = adviceClient?.relationship?.find((r) => r.id === accountHolderCode);
                    dob = rel?.age;
                    age = rel?.retirementAge || DEFAULT_RETIREMENT_AGE;
                }
                const retirement = addAgeToDate(dob, age, STORAGE_DATE_FORMAT);
                return retirement
                    ? dayjs(retirement, STORAGE_DATE_FORMAT).startOf('month').format(STORAGE_DATE_FORMAT)
                    : '';
            },
        [adviceClient]
    );

    const getPlanningIsoDate = useMemo(
        () =>
            (accountHolderCode: string): string => {
                let dob: string | undefined;
                let age: string | number | undefined;
                if (accountHolderCode === 'primary' || accountHolderCode === 'joint') {
                    dob = adviceClient?.clientDob;
                    age = adviceClient?.planningAge || DEFAULT_PLANNING_AGE;
                } else {
                    const rel = adviceClient?.relationship?.find((r) => r.id === accountHolderCode);
                    dob = rel?.age;
                    age = rel?.planningAge || DEFAULT_PLANNING_AGE;
                }
                const planning = addAgeToDate(dob, age, STORAGE_DATE_FORMAT);
                return planning
                    ? dayjs(planning, STORAGE_DATE_FORMAT).startOf('month').format(STORAGE_DATE_FORMAT)
                    : '';
            },
        [adviceClient]
    );

    // Resolve the date building-blocks (current month, retirement date, planning date) for the
    // selected account holder. The actual per-option start/end rules live in
    // `contributionPeriodOptionRules.ts`; this only supplies the holder-specific dates they use.
    const buildContributionPeriodContext = useCallback(
        (accountHolderCode: string, frequency?: string): ContributionPeriodDateContext => {
            const unit: 'year' | 'month' = isMonthYearDatesEnabled()
                ? 'month'
                : frequency === 'annually'
                ? 'year'
                : 'month';
            return {
                currentMonthStart: frequency
                    ? dayjs().add(1, unit).startOf(unit).format(STORAGE_DATE_FORMAT)
                    : dayjs().startOf('month').format(STORAGE_DATE_FORMAT),
                retirementDate: getRetirementIsoDate(accountHolderCode),
                planningDate: getPlanningIsoDate(accountHolderCode)
            };
        },
        [getRetirementIsoDate, getPlanningIsoDate]
    );

    const getContributionPeriodDates = useMemo(
        () =>
            (periodKey: string, accountHolderCode: string, frequency?: string): ContributionPeriodDates =>
                getContributionPeriodDatesFromRules(
                    periodKey,
                    buildContributionPeriodContext(accountHolderCode, frequency)
                ),
        [buildContributionPeriodContext]
    );

    const deriveContributionPeriodKey = useMemo(
        () =>
            (start: string, end: string, accountHolderCode: string, frequency?: string): string =>
                deriveContributionPeriodKeyFromRules(
                    start,
                    end,
                    buildContributionPeriodContext(accountHolderCode, frequency)
                ),
        [buildContributionPeriodContext]
    );

    const formatContributionPeriodRange = useMemo(
        () =>
            (start: string, end: string): string => {
                if (!start || !end) return '-';
                return `${dayjs(start, STORAGE_DATE_FORMAT).format('MMM YYYY')} - ${dayjs(
                    end,
                    STORAGE_DATE_FORMAT
                ).format('MMM YYYY')}`;
            },
        []
    );

    const frequencyOptions: OptionItem[] = useMemo(
        () => [
            { label: t('MONTHLY'), value: 'monthly' },
            { label: t('ANNUALLY'), value: 'annually' },
            { label: t('ONE_TIME'), value: 'onetime' }
        ],
        [t]
    );

    const contributionPeriodOptions: OptionItem[] = useMemo(
        () => CONTRIBUTION_PERIOD_OPTIONS.map((option) => ({ label: t(option.labelKey), value: option.key })),
        [t]
    );

    const contributionTypeOptions: TermOptionItem[] = useMemo(() => {
        const rawTypes = orgInfo?.incomeSourceType?.[language] || orgInfo?.incomeSourceType?.['en'] || [];
        return Array.isArray(rawTypes)
            ? rawTypes.map((item: { label: string; value: string; duration?: string }) => ({
                  label: item.label,
                  value: item.value,
                  duration: item.duration,
                  defaultPeriod: IncomeSorceDefaultPeriodMapping[item.label] || 'Custom'
              }))
            : [];
    }, [orgInfo, language]);

    const renderContributionsSummary = useCallback(
        (count: number) =>
            count === 0 ? (
                <Typography
                    label={t('ADD_CONTRIBUTIONS')}
                    sx={{
                        color: theme.palette.semantic.text.default,
                        fontWeight: 600,
                        lineHeight: '18px',
                        cursor: 'pointer',
                        fontSize: 13
                    }}
                />
            ) : (
                <Typography
                    label={t(count === 1 ? 'ACCOUNTS_SOURCE_SINGULAR' : 'ACCOUNTS_SOURCE_PLURAL', { count })}
                    sx={{
                        color: theme.palette.semantic.text.default,
                        fontWeight: 600,
                        lineHeight: '18px',
                        fontSize: 13
                    }}
                />
            ),
        [t, theme]
    );

    const columns: EditableColumnDef<ClientAccountEntry>[] = useMemo(
        () => [
            {
                key: 'accountType',
                label: t('TYPE'),
                labelInfo: (
                    <InfoTooltip
                        message={<Trans i18nKey="ACCOUNTS_TYPE_TENURE_INFO_TEXT" components={{ br: <br /> }} />}
                    />
                ),
                align: 'left',
                width: '20%',
                sortable: false,
                render: (v) => (
                    <TermTypeCell
                        label={typeLabelByValue[String(v)] ?? String(v || '-')}
                        term={typeTermByValue[String(v)]}
                        variant="bordered"
                        labelSx={{ fontWeight: 400, color: theme.palette.semantic.text.default }}
                    />
                ),
                renderEdit: ({ control, setValue, errors }) => (
                    <Controller
                        name="accountType"
                        control={control}
                        rules={{ required: t('VALIDATION_TYPE_REQUIRED') }}
                        render={({ field }) => (
                            <TermTypeSelect
                                options={typeOptions}
                                value={field.value ?? ''}
                                onChange={(val, term) => {
                                    setDuplicateNameError('');
                                    setSaveErrorMessage('');
                                    field.onChange(val);
                                    setValue('term', term ?? '', { shouldDirty: true });
                                }}
                                error={!!errors.accountType}
                                showTooltipError
                                openTooltipError={!!errors.accountType && Object.keys(errors).length === 1}
                                errorText={errors.accountType?.message as string | undefined}
                            />
                        )}
                    />
                )
            },
            {
                key: 'accountHolder',
                label: t('HOLDER'),
                align: 'left',
                width: '15%',
                sortable: true,
                render: (v) => (
                    <Typography
                        label={accountHolderLabelByValue[String(v)] || String(v || '-')}
                        variant="body.large"
                        sx={{ fontWeight: 400, color: theme.palette.semantic.text.default }}
                    />
                ),
                renderEdit: ({ control, errors }) => (
                    <Controller
                        name="accountHolder"
                        control={control}
                        rules={{ required: t('VALIDATION_HOLDER_REQUIRED') }}
                        render={({ field }) => (
                            <ComboSelect
                                options={accountHolderOptions}
                                selectedValue={field.value ?? ''}
                                onSelectChange={(val) => {
                                    setDuplicateNameError('');
                                    setSaveErrorMessage('');
                                    field.onChange(val);
                                }}
                                size="small"
                                fullWidth
                                error={!!errors.accountHolder}
                                showTooltipError
                                openTooltipError={!!errors.accountHolder && Object.keys(errors).length === 1}
                                errorText={errors.accountHolder?.message as string | undefined}
                            />
                        )}
                    />
                )
            },
            {
                key: 'accountName',
                label: t('NAME'),
                align: 'left',
                width: '22%',
                sortable: true,
                render: (v) => (
                    <Typography
                        label={String(v || '-')}
                        variant="body.large"
                        sx={{ fontWeight: 400, color: theme.palette.semantic.text.default }}
                    />
                ),
                renderEdit: ({ control, setValue, errors }) => (
                    <AutoNameField
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        holderLabelByValue={accountHolderFirstNameByValue}
                        typeLabelByValue={typeLabelByValue}
                        placeholder={t('PLACEHOLDER_ENTER_NAME')}
                        externalError={duplicateNameError}
                        onClearExternalError={() => setDuplicateNameError('')}
                    />
                )
            },
            {
                key: 'amount',
                label: t('CURRENT_BALANCE'),
                align: 'right',
                width: '18%',
                sortable: true,
                render: (v) => (
                    <Typography
                        label={formatCurrency(Number(v) || 0)}
                        variant="body.large"
                        sx={{ textAlign: 'right', fontWeight: 400, color: theme.palette.semantic.text.default }}
                    />
                ),
                renderEdit: ({ control, errors }) => (
                    <Controller
                        name="amount"
                        control={control}
                        rules={{
                            deps: ['contributions'],
                            validate: {
                                required: (v, formValues) => {
                                    const hasContribs = (formValues?.contributions?.length ?? 0) > 0;
                                    if (hasContribs) return true;
                                    return Number(v) > 0 || t('VALIDATION_BALANCE_REQUIRED');
                                },
                                maxDigits: (v) => !exceedsDigitLimit(v, 15) || t('CURRENT_BALANCE_MAXLENGTH_ERROR')
                            }
                        }}
                        render={({ field }) => (
                            <TextboxInput
                                type="currency"
                                size="small"
                                placeholder={currencySymbol}
                                value={field.value ? formatCurrency(+field.value, true) : ''}
                                onChange={(e) => {
                                    const numericValue = removeNonNumeric(e.target.value);
                                    field.onChange(numericValue ? Number(numericValue) : 0);
                                }}
                                min={undefined}
                                error={!!errors.amount}
                                showTooltipError={true}
                                openTooltipError={!!errors.amount && Object.keys(errors).length === 1}
                                helperText={errors.amount?.message as string | undefined}
                                sx={{ '& input': { textAlign: 'right' } }}
                                fullWidth
                            />
                        )}
                    />
                )
            },
            {
                key: 'contributions',
                label: t('CONTRIBUTION_FUNDING'),
                align: 'left',
                width: '15%',
                expandable: true,
                render: (_v, row) => renderContributionsSummary(row.contributions?.length ?? 0),
                // While editing, read the live field-array length so the count reflects
                // in-progress add/remove immediately (before the account row is saved).
                renderEdit: ({ watch }) =>
                    renderContributionsSummary((watch('contributions') as unknown[] | undefined)?.length ?? 0)
            }
        ],
        [
            t,
            theme,
            typeOptions,
            typeLabelByValue,
            typeTermByValue,
            accountHolderOptions,
            accountHolderLabelByValue,
            currencySymbol,
            duplicateNameError,
            renderContributionsSummary
        ]
    );

    const checkIfExists = useCallback(
        (values: ClientAccountEntry): boolean => {
            const normalized = String(values.accountName ?? '')
                .trim()
                .toLowerCase();
            if (!normalized) return false;
            return accounts.some(
                (acc) => acc.localId !== values.localId && (acc.accountName ?? '').trim().toLowerCase() === normalized
            );
        },
        [accounts]
    );

    const onSubmitAccount = useCallback(
        async (values: ClientAccountEntry) => {
            if (checkIfExists(values)) {
                setDuplicateNameError(t('TEXT_ACCOUNT_NAME_EXIST'));
                throw new Error('DUPLICATE_ACCOUNT_NAME');
            }
            setDuplicateNameError('');
            setSaveErrorMessage('');
            try {
                const backendManagedIdsEnabled = checkFFEnabled(
                    FEATURE_NAMES['AEX-3590_3604_NEW_FLOW_UPDATE_SAVE_PLAN']
                );
                const changed = await dispatch(
                    updateAccountState({
                        ...values,
                        localId: values.localId || uuidv4(),
                        ...(!backendManagedIdsEnabled ? {} : { accountId: values.accountId || uuidv4() }),
                        amount: Number(values.amount) || 0,
                        contributions: values.contributions ?? []
                    })
                );
                if (changed) {
                    markPlanStaleForStep1Change();
                }
                setIsAddingNew(false);
            } catch (e) {
                if (!markStaleFromError(e)) {
                    const serverMessage = (e as { response?: { data?: { message?: string } } })?.response?.data
                        ?.message;
                    setSaveErrorMessage(
                        serverMessage
                            ? t(serverMessage, { defaultValue: serverMessage })
                            : t('SAVE_PLAN_FAILED_BANNER')
                    );
                }
                throw e;
            }
        },
        [dispatch, t, checkIfExists, markPlanStaleForStep1Change, markStaleFromError]
    );

    return (
        <Box>
            <div
                style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '32px', alignItems: 'center' }}
            >
                <Typography
                    variant="body.large"
                    label={t('ACCOUNTS_INLINE_SUBTITLE')}
                    color="semantic.text.default"
                    sx={{ fontWeight: 700, fontSize: '14px', color: theme.palette.semantic.text.default }}
                />
                <Button
                    type="button"
                    variant="outlined"
                    onClick={() => setIsAddingNew(true)}
                    disabled={hasOpenEdit}
                    sx={{ borderRadius: '24px', textTransform: 'none' }}
                >
                    {t('ADD_ACCOUNT')}
                </Button>
            </div>
            {isStalePlan && <StalePlanBanner onRefresh={refreshPlan} />}
            {saveErrorMessage && (
                <Box sx={{ paddingBlock: 1 }}>
                    <PdfDownloadErrorBanner i18nKey="SAVE_PLAN_FAILED_BANNER" />
                </Box>
            )}
            <EditableTable<ClientAccountEntry>
                rows={accounts}
                columns={columns}
                rowKey="localId"
                actionsColumnLabel={t('ACTIONS')}
                rowHeight="50px"
                onSaveRow={onSubmitAccount}
                onDeleteRow={(row) => setPendingDeleteAccount(row)}
                saveError={!!saveErrorMessage}
                isAddingNew={isAddingNew}
                onCancelAdd={() => {
                    setIsAddingNew(false);
                    setDuplicateNameError('');
                    setSaveErrorMessage('');
                }}
                onClearSaveError={() => setSaveErrorMessage('')}
                onDirtyStateChange={handleDirtyStateChange}
                onEditingChange={handleEditingChange}
                cancelConfirm={{
                    title: t('DRAWER_CLOSE_MODAL_TITLE'),
                    content: t('DRAWER_CLOSE_MODAL_HEADER_TEXT'),
                    stayLabel: t('DRAWER_CLOSE_MODAL_HEADER_TEXT_STAY'),
                    leaveLabel: t('DRAWER_CLOSE_MODAL_HEADER_TEXT_LEAVE')
                }}
                skipSaveIfUnchanged
                newRowDefaults={{
                    localId: '',
                    accountType: typeOptions[0]?.value ?? '',
                    term: typeOptions[0]?.duration ?? '',
                    accountHolder: accountHolderOptions[0]?.value ?? '',
                    accountName: formatAccountName(
                        typeOptions[0]?.label ?? '',
                        accountHolderFirstNameByValue[accountHolderOptions[0]?.value ?? ''] ?? ''
                    ),
                    amount: null,
                    contributions: []
                }}
                expandOnEdit={(row) => (row.contributions?.length ?? 0) > 0}
                renderExpanded={(_row, { form, startEditing, isLocked }) =>
                    form ? (
                        <ContributionsEditor
                            form={form as RowEditContext<ClientAccountEntry>}
                            onPromoteAccount={startEditing}
                            typeOptions={contributionTypeOptions}
                            accountHolderOptions={accountHolderOptions}
                            accountHolderLabelByValue={accountHolderLabelByValue}
                            frequencyOptions={frequencyOptions}
                            contributionPeriodOptions={contributionPeriodOptions}
                            getContributionPeriodDates={getContributionPeriodDates}
                            deriveContributionPeriodKey={deriveContributionPeriodKey}
                            formatContributionPeriodRange={formatContributionPeriodRange}
                            currencySymbol={currencySymbol}
                            readOnly={isLocked}
                        />
                    ) : null
                }
                ariaLabels={{
                    edit: t('BUTTON_EDIT'),
                    delete: t('DELETE_TEXT'),
                    save: t('BUTTON_SAVE'),
                    cancel: t('BUTTON_CANCEL')
                }}
            />
            {pendingDeleteAccount && (
                <ConfirmationCentered
                    open
                    title={t('DELETE_ACCOUNT_TITLE')}
                    content={t(
                        checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428'])
                            ? 'DELETE_ACCOUNT_CONTENT_FTDS'
                            : 'DELETE_ACCOUNT_CONTENT'
                    )}
                    confirmButtonLabel={t('DELETE_TEXT')}
                    cancelButtonLabel={t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}
                    onConfirmation={async () => {
                        dispatch(deleteClientAccount(pendingDeleteAccount.localId));
                        setPendingDeleteAccount(null);
                        dispatch(startLoading());
                        try {
                            const changed = await dispatch(persistClientAccounts());
                            if (changed) markPlanStaleForStep1Change();
                        } catch (error) {
                            console.error('Error deleting account:', error);
                        } finally {
                            dispatch(stopLoading());
                        }
                    }}
                    onCancellation={() => setPendingDeleteAccount(null)}
                    onCloseIconClick={() => setPendingDeleteAccount(null)}
                />
            )}
        </Box>
    );
};

export default AccountsInlineTable;
